import React from "react";
import { User } from "lucide-react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { formatCPF, formatPhoneBRLive } from "../../lib/utils";

const estadoCivilOptions = [
  { value: "Solteiro(a)" },
  { value: "Casado(a)" },
  { value: "Divorciado(a)" },
  { value: "Viúvo(a)" },
];

export function PersonalDataForm({
  userData,
  handleUserDataChange,
  errors,
  isAdminMode,
  setIsFalecido,
}) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center shadow-md">
          <User className="w-5 h-5 text-white" />
        </div>

        <h2 className="text-2xl font-semibold text-slate-800">
          Dados Pessoais
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <Label htmlFor="nome" className="text-sm font-medium text-slate-700">
            Nome Completo
          </Label>

          <Input
            id="nome"
            value={userData.nomeCompleto}
            onChange={(e) => handleUserDataChange("nomeCompleto", e.target.value)}
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.nomeCompleto
                ? "border-red-400 focus:border-red-600"
                : "border-slate-200 focus:border-blue-800"
            }`}
          />

          {errors.nomeCompleto && (
            <p className="mt-1 text-xs text-red-600">{errors.nomeCompleto}</p>
          )}
        </div>
        <div className="md:col-span-2">
          <Label
            htmlFor="falecido"
            className="text-sm font-medium text-slate-700"
          >
            O substituído é falecido?
          </Label>
          <RadioGroup
            id="falecido"
            value={userData.falecido ? "sim" : "nao"}
            onValueChange={(value) => {
              handleUserDataChange("falecido", value === "sim");
              if (value === "sim") setIsFalecido(true);
              else setIsFalecido(false);
            }}
            className="flex space-x-4 mt-2"
            disabled={userData.falecido === true && !isAdminMode}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="sim" id="falecido-sim" />
              <Label htmlFor="falecido-sim">Sim</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="nao" id="falecido-nao" />
              <Label htmlFor="falecido-nao">Não</Label>
            </div>
          </RadioGroup>
        </div>

        <div>
          <Label htmlFor="cpf" className="text-sm font-medium text-slate-700">
            CPF
          </Label>

          <Input
            id="cpf"
            value={userData.cpf}
            readOnly={!isAdminMode}
            onChange={(e) =>
              handleUserDataChange("cpf", formatCPF(e.target.value))
            }
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.cpf
                ? "border-red-400 focus:border-red-600"
                : isAdminMode
                ? "border-slate-200 focus:border-blue-800"
                : "bg-slate-100 border-slate-200 cursor-not-allowed"
            }`}
          />

          {errors.cpf && (
            <p className="mt-1 text-xs text-red-600">{errors.cpf}</p>
          )}
        </div>

        <div>
          <Label htmlFor="email" className="text-sm font-medium text-slate-700">
            E-mail
          </Label>

          <Input
            id="email"
            type="email"
            required
            value={userData.email}
            onChange={(e) => handleUserDataChange("email", e.target.value)}
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.email
                ? "border-red-400 focus:border-red-600"
                : "border-slate-200 focus:border-blue-800"
            }`}
          />

          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email}</p>
          )}
        </div>

        <div>
          <Label htmlFor="rg" className="text-sm font-medium text-slate-700">
            RG
          </Label>

          <Input
            id="rg"
            value={userData.rg || ""}
            onChange={(e) => handleUserDataChange("rg", e.target.value)}
            required
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.rg
                ? "border-red-400 focus:border-red-600"
                : "border-slate-200 focus:border-blue-800"
            }`}
          />

          {errors.rg && <p className="mt-1 text-xs text-red-600">{errors.rg}</p>}
        </div>

        <div>
          <Label
            htmlFor="data_nascimento"
            className="text-sm font-medium text-slate-700"
          >
            Data de Nascimento
          </Label>

          <Input
            id="data_nascimento"
            type="date"
            value={userData.data_nascimento || ""}
            onChange={(e) =>
              handleUserDataChange("data_nascimento", e.target.value)
            }
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.data_nascimento
                ? "border-red-400 focus:border-red-600"
                : "border-slate-200 focus:border-blue-800"
            }`}
          />

          {errors.data_nascimento && (
            <p className="mt-1 text-xs text-red-600">
              {errors.data_nascimento}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="telefone" className="text-sm font-medium text-slate-700">
            Telefone
          </Label>

          <Input
            id="telefone"
            type="tel"
            required
            placeholder="(00) 90000-0000"
            value={userData.telefone || ""}
            onChange={(e) =>
              handleUserDataChange("telefone", formatPhoneBRLive(e.target.value))
            }
            className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
              errors.telefone
                ? "border-red-400 focus:border-red-600"
                : "border-slate-200 focus:border-blue-800"
            }`}
          />

          {errors.telefone && (
            <p className="mt-1 text-xs text-red-600">{errors.telefone}</p>
          )}
        </div>

        <div>
          <Label
            htmlFor="estadoCivil"
            className="text-sm font-medium text-slate-700"
          >
            Estado Civil
          </Label>

          <Select
            onValueChange={(value) => handleUserDataChange("estadoCivil", value)}
            value={userData.estadoCivil}
          >
            <SelectTrigger
              className={`w-full mt-2 border-2 h-11 focus:ring-2 focus:ring-blue-800/20 transition-colors ${
                errors.estadoCivil
                  ? "border-red-400 focus:border-red-600"
                  : "border-slate-200 focus:border-blue-800"
              }`}
            >
              <SelectValue placeholder="Selecione o estado civil..." />
            </SelectTrigger>

            <SelectContent>
              {estadoCivilOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {errors.estadoCivil && (
            <p className="mt-1 text-xs text-red-600">{errors.estadoCivil}</p>
          )}
        </div>

        {userData.estadoCivil === "Casado(a)" && (
          <>
            <div className="md:col-span-2">
              <Label
                htmlFor="nome_conjuge"
                className="text-sm font-medium text-slate-700"
              >
                Nome do Cônjuge
              </Label>

              <Input
                id="nome_conjuge"
                value={userData.nome_conjuge || ""}
                onChange={(e) =>
                  handleUserDataChange("nome_conjuge", e.target.value)
                }
                className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
                  errors.nome_conjuge
                    ? "border-red-400 focus:border-red-600"
                    : "border-slate-200 focus:border-blue-800"
                }`}
              />

              {errors.nome_conjuge && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.nome_conjuge}
                </p>
              )}
            </div>

            <div>
              <Label
                htmlFor="cpf_conjuge"
                className="text-sm font-medium text-slate-700"
              >
                CPF do Cônjuge
              </Label>

              <Input
                id="cpf_conjuge"
                value={userData.cpf_conjuge || ""}
                onChange={(e) =>
                  handleUserDataChange("cpf_conjuge", formatCPF(e.target.value))
                }
                className={`mt-2 border-2 transition-colors h-11 focus:ring-2 focus:ring-blue-800/20 ${
                  errors.cpf_conjuge
                    ? "border-red-400 focus:border-red-600"
                    : "border-slate-200 focus:border-blue-800"
                }`}
              />

              {errors.cpf_conjuge && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.cpf_conjuge}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
